// Firebase Authentication Service Layer
// Equivalent to lib/supabaseAuth.ts but for Firebase Auth

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  User,
  UserCredential,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseAuth, getFirebaseFunctions } from './firebase';
import { staffService } from './database';
import { config } from './config';

const auth = getFirebaseAuth();

export interface SignUpData {
  email: string;
  password: string;
  fullName: string;
  role: string;
  pin?: string;
}

export interface SignInData {
  email: string;
  password: string;
}

export interface CreateStaffUserData {
  email: string;
  password: string;
  staffFirestoreId: string;
  isAdmin: boolean;
  displayName?: string;
}

export const authService = {
  /**
   * Create a staff Auth user via Cloud Function (admin only).
   * Sets custom claims so the new user can use the app without running the claims script.
   * Does not sign in as the new user – current admin stays signed in.
   */
  async createStaffUserViaFunction(data: CreateStaffUserData): Promise<{ success: true }> {
    const functions = getFirebaseFunctions();
    const createStaffUser = httpsCallable<
      { email: string; password: string; staffFirestoreId: string; isAdmin: boolean; displayName?: string },
      { success: boolean; uid?: string }
    >(functions, 'createStaffUser');
    const result = await createStaffUser({
      email: data.email.trim().toLowerCase(),
      password: data.password,
      staffFirestoreId: data.staffFirestoreId,
      isAdmin: data.isAdmin,
      displayName: data.displayName ?? data.email.split('@')[0],
    });
    const response = result.data;
    if (!response?.success) {
      throw new Error('Failed to create staff user');
    }
    return { success: true };
  },

  /**
   * Sign up a new user with email and password
   */
  async signUp(data: SignUpData, existingStaffId?: string): Promise<{ user: User | null; error: any }> {
    try {
      const userCredential: UserCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      const user = userCredential.user;

      if (user) {
        // Update or create staff record
        if (existingStaffId) {
          // Get existing staff record first to preserve important fields like isAdmin
          const existingStaff = await staffService.getById(existingStaffId);
          if (!existingStaff) {
            console.error(`Staff record with ID ${existingStaffId} not found. Cannot link auth user.`);
            throw new Error(`Staff record with ID ${existingStaffId} not found. Cannot link auth user.`);
          }
          
          console.log('Updating existing staff record:', existingStaffId, 'with auth user:', user.uid);
          
          // Update existing staff with Firebase UID, preserving important fields.
          // NOTE: In production, this update can fail with Firestore PERMISSION_DENIED
          // if the current auth session has just switched to the new user (no custom claims yet).
          // In that case, we log the error but do not treat it as a hard failure, because:
          // - The staff record itself has already been created/verified.
          // - Linking authUserId is a convenience and can be done later by scripts.
          const updates: any = {
            authUserId: user.uid,
            email: data.email.toLowerCase(),
            emailVerified: false,
          };
          
          // Preserve isAdmin if it was set
          if (existingStaff.isAdmin !== undefined) {
            updates.isAdmin = existingStaff.isAdmin;
          }
          
          // Preserve role if it was set (should already be set, but just in case)
          if (existingStaff.role) {
            updates.role = existingStaff.role;
          }
          
          try {
            await staffService.update(existingStaffId, updates);
            console.log('Staff record updated successfully');
          } catch (updateError: any) {
            console.error('Failed to update staff record after auth signup (non-fatal):', updateError);
          }
        } else {
          // Create new staff record
          await staffService.create({
            name: data.fullName,
            role: data.role,
            pin: data.pin || '0000',
            email: data.email.toLowerCase(),
            authUserId: user.uid,
            emailVerified: false,
            isAdmin: false,
          } as any);
        }
      }

      return { user, error: null };
    } catch (error: any) {
      console.error('Firebase sign up error:', error);
      
      // Handle email already in use - try to link existing user
      if (error.code === 'auth/email-already-in-use' && existingStaffId) {
        try {
          // Try to get the existing user and link it
          const userRecord = await auth.currentUser;
          if (userRecord) {
            await staffService.update(existingStaffId, {
              authUserId: userRecord.uid,
              emailVerified: userRecord.emailVerified,
            } as any);
            console.log('Linked existing Firebase user to staff record');
            return { user: userRecord, error: null };
          }
        } catch (linkError) {
          console.error('Failed to link existing user:', linkError);
        }
      }
      
      return { user: null, error };
    }
  },

  /**
   * Sign in with email and password
   */
  async signIn(data: SignInData): Promise<{ user: User | null; error: any }> {
    try {
      const userCredential: UserCredential = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      const user = userCredential.user;

      if (user) {
        // Force refresh ID token so custom claims (staffId, isAdmin) are included
        await user.getIdToken(true);

        // After successful sign-in, try to link authUserId if it's missing
        try {
          const staffRecord = await staffService.getByEmail(data.email);
          if (staffRecord && !staffRecord.authUserId) {
            const staffDocId = (staffRecord as any).firestoreId || String(staffRecord.id);
            await staffService.update(staffDocId, {
              authUserId: user.uid,
            } as any);
            console.log('Successfully linked Firebase UID on sign-in');
          }
        } catch (err) {
          console.warn('Could not link Firebase UID on sign-in (will retry next time):', err);
        }
      }

      return { user, error: null };
    } catch (error: any) {
      console.error('Firebase sign in error:', error);
      return { user: null, error };
    }
  },

  /**
   * Sign out the current user
   */
  async signOut(): Promise<{ error: any }> {
    try {
      await firebaseSignOut(auth);
      return { error: null };
    } catch (error: any) {
      console.error('Firebase sign out error:', error);
      return { error };
    }
  },

  /**
   * Get the current user
   */
  async getUser(): Promise<{ user: User | null; error: any }> {
    try {
      const user = auth.currentUser;
      return { user, error: null };
    } catch (error: any) {
      console.error('Firebase get user error:', error);
      return { user: null, error };
    }
  },

  /**
   * Send password reset email
   * Uses Resend if configured, otherwise falls back to Firebase
   */
  async resetPassword(email: string): Promise<{ error: any }> {
    try {
      // Check if Resend is configured
      const resendApiKey = import.meta.env.VITE_RESEND_API_KEY;
      
      if (resendApiKey) {
        // Use Resend for custom branded emails
        const { emailService } = await import('./email');
        
        // Generate Firebase password reset link
        const actionCodeSettings = {
          url: `${window.location.origin}/signin?mode=resetPassword`,
          handleCodeInApp: false,
        };
        
        // Get the reset link from Firebase
        // Note: Firebase doesn't provide the link directly, so we use Firebase's sendPasswordResetEmail
        // but then send a custom email via Resend with a better template
        // For now, we'll use Firebase's built-in reset and send a notification via Resend
        // In production, you might want to implement a custom reset token system
        
        // Fallback to Firebase for now (Resend can be used for notifications)
        await sendPasswordResetEmail(auth, email);
        
        // Optionally send a branded notification via Resend
        try {
          const resetLink = `${window.location.origin}/signin?mode=resetPassword&email=${encodeURIComponent(email)}`;
          await emailService.sendPasswordReset(email, resetLink);
        } catch (resendError) {
          // If Resend fails, Firebase email was still sent, so log but don't fail
          console.warn('Resend notification failed, but Firebase reset email was sent:', resendError);
        }
        
        return { error: null };
      } else {
        // Fallback to Firebase
        await sendPasswordResetEmail(auth, email);
        return { error: null };
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      return { error };
    }
  },

  /**
   * Update user password (requires user to be signed in)
   */
  async updatePassword(newPassword: string): Promise<{ error: any }> {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { error: new Error('No user is currently signed in') };
      }
      await firebaseUpdatePassword(user, newPassword);
      return { error: null };
    } catch (error: any) {
      console.error('Firebase update password error:', error);
      return { error };
    }
  },

  /**
   * Get staff member by Firebase UID
   */
  async getStaffByAuthUserId(authUserId: string): Promise<any> {
    try {
      const allStaff = await staffService.getAll();
      const staff = allStaff.find((s: any) => s.authUserId === authUserId);
      return staff || null;
    } catch (error: any) {
      // Only log as error if it's not a "not found" scenario
      // Staff not found is expected in some cases (e.g., during initial auth state check)
      if (error?.code !== 'not-found' && error?.message?.includes('not found') === false) {
        console.error('Get staff by auth user ID error:', error);
      }
      return null;
    }
  },

  /**
   * Get staff member by email
   */
  async getStaffByEmail(email: string): Promise<any> {
    try {
      return await staffService.getByEmail(email);
    } catch (error: any) {
      // Only log as error if it's not a "not found" scenario
      // Staff not found is expected in some cases (e.g., during email validation)
      if (error?.code !== 'not-found' && error?.message?.includes('not found') === false) {
        console.error('Get staff by email error:', error);
      }
      return null;
    }
  },

  /**
   * Check if 2FA is enabled for current user
   */
  async hasTwoFactorEnabled(): Promise<{ enabled: boolean; factors: any[] }> {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { enabled: false, factors: [] };
      }

      // Get staff by Firebase UID
      const staff = await this.getStaffByAuthUserId(user.uid);
      if (!staff) {
        return { enabled: false, factors: [] };
      }

      // Use Firestore document ID for 2FA (must match token staffId for rules)
      const staffIdFor2FA = (staff as { firestoreId?: string }).firestoreId ?? staff.id.toString();
      const { hasStaff2FA } = await import('./twoFactor');
      const has2FA = await hasStaff2FA(staffIdFor2FA);

      return { 
        enabled: has2FA, 
        factors: has2FA ? [{ id: 'totp', type: 'totp' }] : [] 
      };
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      return { enabled: false, factors: [] };
    }
  },

  /**
   * Verify 2FA code during sign-in
   */
  async verifyTwoFactor(staffId: string, code: string): Promise<{ verified: boolean; error: any }> {
    try {
      const { verifyStaff2FA } = await import('./twoFactor');
      const result = await verifyStaff2FA(staffId, code);
      
      if (result.success) {
        return { verified: true, error: null };
      }
      
      return { verified: false, error: new Error(result.error || 'Invalid code') };
    } catch (error: any) {
      console.error('2FA verification error:', error);
      return { verified: false, error };
    }
  },

  /**
   * Set up 2FA for current user (start the setup process)
   */
  async setupTwoFactor(): Promise<{ data: any; error: any }> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        return { data: null, error: new Error('No user is currently signed in') };
      }

      // Get staff by Firebase UID
      const staff = await this.getStaffByAuthUserId(user.uid);
      if (!staff) {
        return { data: null, error: new Error('Staff record not found') };
      }

      // Use Firestore document ID for 2FA (must match token staffId for rules)
      const staffIdFor2FA = (staff as { firestoreId?: string }).firestoreId ?? staff.id.toString();
      const { startStaff2FASetup } = await import('./twoFactor');
      const setupData = await startStaff2FASetup(staffIdFor2FA, user.email);

      return { 
        data: {
          secret: setupData.secret,
          qrCodeUrl: setupData.qrCodeUrl,
          qrCodeDataUrl: setupData.qrCodeDataUrl,
        }, 
        error: null 
      };
    } catch (error: any) {
      console.error('2FA setup error:', error);
      return { data: null, error };
    }
  },

  /**
   * Complete 2FA setup (verify and enable)
   */
  async completeTwoFactorSetup(code: string): Promise<{ success: boolean; backupCodes?: string[]; error?: string }> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        return { success: false, error: 'No user is currently signed in' };
      }

      // Get staff by Firebase UID
      const staff = await this.getStaffByAuthUserId(user.uid);
      if (!staff) {
        return { success: false, error: 'Staff record not found' };
      }

      // Use Firestore document ID for 2FA (must match token staffId for rules)
      const staffIdFor2FA = (staff as { firestoreId?: string }).firestoreId ?? staff.id.toString();
      const { enableStaff2FA } = await import('./twoFactor');
      return await enableStaff2FA(staffIdFor2FA, user.email, code);
    } catch (error: any) {
      console.error('2FA enable error:', error);
      return { success: false, error: error.message || 'Failed to enable 2FA' };
    }
  },

  /**
   * Send invitation email to staff member
   * Uses Resend for branded emails if configured
   */
  async sendInvitation(email: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      throw new Error('Email is required to send an invitation.');
    }

    const staff = await staffService.getByEmail(normalizedEmail);
    if (!staff) {
      throw new Error('No staff member found with this email.');
    }

    const staffId = staff.id !== undefined ? String(staff.id) : '';
    if (!staffId) {
      throw new Error('Staff record is missing an ID. Please edit the staff member and try again.');
    }

    // Ensure Firebase Auth user exists so reset email can be sent
    if (!staff.authUserId) {
      await ensureFirebaseUserForStaff(normalizedEmail, staffId);
    }

    // Check if Resend is configured
    const resendApiKey = import.meta.env.VITE_RESEND_API_KEY;
    
    if (resendApiKey) {
      // Use Resend for branded invitation email
      const { emailService } = await import('./email');
      
      // Generate password reset link (Firebase will handle the actual reset)
      // We'll use Firebase's password reset, but send a branded email via Resend
      const resetLink = `${window.location.origin}/signin?mode=resetPassword&email=${encodeURIComponent(normalizedEmail)}`;
      
      // Send branded invitation via Resend
      const result = await emailService.sendStaffInvitation(
        normalizedEmail,
        staff.name || 'Team Member',
        resetLink
      );
      
      if (!result.success) {
        console.warn('Resend invitation failed, falling back to Firebase:', result.error);
        // Fallback to Firebase
        const { error } = await this.resetPassword(normalizedEmail);
        if (error) {
          throw error;
        }
      } else {
        // Also send Firebase reset email for the actual reset link
        // This ensures the reset link works properly
        await sendPasswordResetEmail(auth, normalizedEmail);
      }
    } else {
      // Fallback to Firebase only
      const { error } = await this.resetPassword(normalizedEmail);
      if (error) {
        throw error;
      }
    }
  },

  /**
   * Disable 2FA for current user
   */
  async disableTwoFactor(): Promise<{ success: boolean; error?: string }> {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { success: false, error: 'No user is currently signed in' };
      }

      // Get staff by Firebase UID
      const staff = await this.getStaffByAuthUserId(user.uid);
      if (!staff) {
        return { success: false, error: 'Staff record not found' };
      }

      // Use Firestore document ID for 2FA (must match token staffId for rules)
      const staffIdFor2FA = (staff as { firestoreId?: string }).firestoreId ?? staff.id.toString();
      const { disableStaff2FA } = await import('./twoFactor');
      return await disableStaff2FA(staffIdFor2FA);
    } catch (error: any) {
      console.error('2FA disable error:', error);
      return { success: false, error: error.message || 'Failed to disable 2FA' };
    }
  },

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (user: User | null) => void): () => void {
    return auth.onAuthStateChanged(callback);
  },
};

async function ensureFirebaseUserForStaff(email: string, staffId: string) {
  const apiKey = config.firebase.apiKey;
  if (!apiKey) {
    throw new Error('Firebase API key is not configured. Please update your environment variables.');
  }

  const tempPassword = generateTemporaryPassword();
  const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      password: tempPassword,
      returnSecureToken: false,
    }),
  });

  const result = await response.json();
  if (!response.ok) {
    if (result?.error?.message === 'EMAIL_EXISTS') {
      return;
    }
    const message = result?.error?.message || 'Failed to create Firebase auth user';
    throw new Error(message);
  }

  if (result?.localId) {
    await staffService.update(staffId, {
      authUserId: result.localId,
      emailVerified: false,
    } as any);
  }
}

function generateTemporaryPassword(): string {
  const base = Math.random().toString(36).slice(-8);
  const special = '!@#$%';
  const number = Math.floor(Math.random() * 10);
  return `Temp${base}${special[number % special.length]}${number}`;
}
